import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChatMessage, isPushAction } from "@/entities/messaging/types";
import { useRefreshFilteredTasks } from "@/entities/tasks/hooks";

// The task id of a Dreamer task-change push, or undefined for any other message.
const dreamerTaskChangeId = (chatMessage: ChatMessage) => {
  const action =
    chatMessage.content?.kind === "action"
      ? chatMessage.content.action
      : undefined;
  if (
    isPushAction(action) &&
    (action.type === "dreamer_task_undo" ||
      action.type === "dreamer_task_reopen")
  ) {
    return action.taskId ?? "";
  }
  return undefined;
};

// Refreshes task lists in place when Dreamer reports it changed a task, so
// lists already on screen keep their rows instead of blanking. Also refetches
// each changed task, since Dreamer's task cards read it to decide whether to show.
export const useRefreshTasksOnDreamerTaskChange = () => {
  const queryClient = useQueryClient();
  const refreshTasks = useRefreshFilteredTasks();
  return useCallback(
    (messages: ChatMessage[]) => {
      const changedIds = messages
        .map(dreamerTaskChangeId)
        .filter((taskId) => taskId !== undefined);
      if (changedIds.length === 0) return;
      refreshTasks();
      new Set(changedIds.filter(Boolean)).forEach((taskId) => {
        queryClient.invalidateQueries({
          queryKey: ["tasks/update", { pathParams: { taskId } }]
        });
      });
    },
    [queryClient, refreshTasks]
  );
};
